/**
 * Serviço para buscar planos de assinatura com base na tabela de preço do usuário.
 * Os preços exibidos e cobrados devem sempre refletir a tabela de preço vigente
 * na data de cadastro do usuário (price_table_id em profiles).
 */
import { supabase } from "@/integrations/supabase/client";

export interface SubscriptionPlanWithPrice {
  code: string;
  name: string;
  min_patients: number;
  max_patients: number | null;
  max_file_uploads: number | null;
  max_connected_users: number | null;
  price_cents: number;
  display_order: number;
  description: string | null;
  features: string[];
  [key: string]: unknown;
}

export interface FetchPlansOptions {
  /** Excluir plano free da lista */
  excludeFree?: boolean;
  /** Ordenar por: display_order ou price_cents */
  orderBy?: "display_order" | "price_cents";
}

/**
 * Busca os planos de assinatura com os preços da tabela do usuário.
 * Se o usuário tiver price_table_id no perfil, usa price_table_plans.
 * Caso contrário, usa os preços padrão de subscription_plans.
 */
export async function fetchPlansForUser(
  userId: string,
  options: FetchPlansOptions = {}
): Promise<SubscriptionPlanWithPrice[]> {
  const { excludeFree = false, orderBy = "display_order" } = options;

  // 1. Buscar price_table_id do perfil
  const { data: profile } = await supabase
    .from("profiles")
    .select("price_table_id")
    .eq("id", userId)
    .maybeSingle();

  const priceTableId = profile?.price_table_id ?? null;

  // 2. Buscar planos base
  let query = supabase
    .from("subscription_plans")
    .select("*")
    .order(orderBy);

  if (excludeFree) {
    query = query.neq("code", "free");
  }

  const { data: plansData, error } = await query;

  if (error) throw error;

  const plans = plansData ?? [];

  // 3. Se tem tabela de preço, buscar preços customizados
  let priceMap: Record<string, number> = {};
  if (priceTableId) {
    const { data: ptPlans } = await supabase
      .from("price_table_plans")
      .select("plan_code, price_cents")
      .eq("price_table_id", priceTableId);

    if (ptPlans) {
      priceMap = Object.fromEntries(
        ptPlans.map((p) => [p.plan_code, p.price_cents])
      );
    }
  }

  // 4. Mesclar: usar preço da tabela se existir, senão subscription_plans
  return plans.map((plan) => ({
    ...plan,
    price_cents: priceMap[plan.code] ?? plan.price_cents,
    features: Array.isArray(plan.features) ? (plan.features as string[]) : [],
  })) as SubscriptionPlanWithPrice[];
}

/**
 * Busca um único plano pelo code com o preço da tabela do usuário.
 */
export async function fetchPlanForUser(
  userId: string,
  planCode: string
): Promise<SubscriptionPlanWithPrice | null> {
  const plans = await fetchPlansForUser(userId, { excludeFree: false });
  return plans.find((p) => p.code === planCode) ?? null;
}
